/**
 * Browser History Handler - Handles browser back/forward navigation
 * Restores search form state from URL when user navigates back/forward
 */

let isRestoringState = false; // Flag to prevent URL updates during state restoration

/**
 * Restore application state from current URL
 * This function reads URL parameters and restores UI state, same as initial page load
 */
function restoreStateFromUrl() {
    try {
        // Set flag to prevent URL updates during restoration
        isRestoringState = true;
        window.__isRestoringBrowserState = true;
        
        // Extract parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('sq') || '';
        const page = urlParams.get('page') || '1';
        const cat = urlParams.get('cat') || '';
        const dateFrom = urlParams.get('dateFrom') || '';
        const dateTo = urlParams.get('dateTo') || '';
        const pu = urlParams.get('pu') || '';
        const hash = window.location.hash;
        
        // Restore search query in input field
        const $searchInput = window.jQuery ? window.jQuery('#fnd_searchbox, input[name="sq"], input[name="search"]').first() : null;
        if ($searchInput && $searchInput.length) {
            $searchInput.val(query);
            window.QUERY = query;
        }
        
        // Restore date filters
        const $dateFrom = window.jQuery ? window.jQuery('.filter-date-from') : null;
        if ($dateFrom && $dateFrom.length) {
            $dateFrom.val(dateFrom || '');
        }
        
        const $dateTo = window.jQuery ? window.jQuery('.filter-date-to') : null;
        if ($dateTo && $dateTo.length) {
            $dateTo.val(dateTo || '');
        }
        
        // Sync date filters with DateFiltersManager for proper integration
        // Pass skipUrlUpdate=true to prevent URL updates during state restoration
        if (window.dateFiltersManager && typeof window.dateFiltersManager.setDateRange === 'function') {
            try {
                window.dateFiltersManager.setDateRange(dateFrom || '', dateTo || '', true); // true = skip URL update
            } catch (e) {
                if (debugMode === '1') {
                    console.warn('[BrowserHistoryHandler] Could not sync dates with DateFiltersManager:', e);
                }
            }
        }
        
        // Sync date filters with LiteDateRangePicker if available
        if (window.__drp && typeof window.__drp.applyRange === 'function') {
            try {
                const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
                const toDate = dateTo ? new Date(dateTo + 'T00:00:00') : null;
                window.__drp.applyRange(fromDate, toDate, 'restore');
            } catch (e) {
                if (debugMode === '1') {
                    console.warn('[BrowserHistoryHandler] Could not sync dates with LiteDateRangePicker:', e);
                }
            }
        }
        
        // Restore public only filter checkbox (only for guests)
        const isAuthenticated = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
        if (!isAuthenticated) {
            const publicOnlyCheckbox = document.getElementById('public-only-filter');
            if (publicOnlyCheckbox) {
                // pu=1 means checked, pu=0 means unchecked
                publicOnlyCheckbox.checked = pu === '1';
                // Update filterManager activeFilters
                if (window.filterManager) {
                    window.filterManager.activeFilters.publicOnly = pu === '1';
                }
            }
        }
        
        // Restore filter controller state
        if (window.filterController) {
            if (window.filterController.activeFilters) {
                window.filterController.activeFilters.searchQuery = query;
                window.filterController.activeFilters.dateFrom = dateFrom;
                window.filterController.activeFilters.dateTo = dateTo;
            }
            
            // Restore categories if filter metadata is available
            if (cat && window.__filterMetadata && window.__filterMetadata.uniqueValues) {
                const values = String(cat).split('-');
                const uniqueValues = window.__filterMetadata.uniqueValues;
                const restoredCategories = {};
                
                for (const val of values) {
                    const trimmed = val.trim();
                    if (!trimmed) continue;
                    
                    // Match value against uniqueValues to determine group
                    for (const [group, groupValues] of Object.entries(uniqueValues)) {
                        if (Array.isArray(groupValues) && groupValues.includes(trimmed)) {
                            if (!restoredCategories[group]) {
                                restoredCategories[group] = [];
                            }
                            if (!restoredCategories[group].includes(trimmed)) {
                                restoredCategories[group].push(trimmed);
                            }
                            break;
                        }
                    }
                }
                
                // Apply restored categories to filter controller
                // IMPORTANT: Always set categories from URL, even if empty - this clears categories not in URL
                window.filterController.activeFilters.categories = restoredCategories;
                
                // Update UI checkboxes/selects for ALL groups
                // First clear all groups, then set only those from URL
                try {
                    if (window.filterController && window.filterController.trees) {
                        // Get all available groups
                        const allGroups = Object.keys(window.filterController.trees);
                        
                        // Update each group: set values from restoredCategories or empty array if not in URL
                        for (const group of allGroups) {
                            const tree = window.filterController.trees[group];
                            if (!tree) continue;
                            
                            // Get values for this group from restored categories (or empty array)
                            const values = restoredCategories[group] || [];
                            
                            if (typeof tree.updateValue === 'function') {
                                tree.updateValue(values);
                            } else if (tree.allValue !== undefined) {
                                tree.allValue = [...values];
                                if (tree.mount) {
                                    tree.mount();
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.warn('[BrowserHistoryHandler] Could not update filter UI:', e);
                }
                
                // Update availability and counter
                if (window.filterController && typeof window.filterController.refreshAvailability === 'function') {
                    window.filterController.refreshAvailability();
                }
                if (window.filterController && typeof window.filterController.updateFilterCounter === 'function') {
                    window.filterController.updateFilterCounter();
                }
            } else if (!cat) {
                // Clear categories if no cat parameter
                if (window.filterController.activeFilters) {
                    window.filterController.activeFilters.categories = {};
                }
                // Clear UI checkboxes
                try {
                    if (window.filterController && window.filterController.trees) {
                        for (const [group, tree] of Object.entries(window.filterController.trees)) {
                            if (tree && typeof tree.updateValue === 'function') {
                                tree.updateValue([]);
                            } else if (tree && tree.allValue !== undefined) {
                                tree.allValue = [];
                                if (tree.mount) {
                                    tree.mount();
                                }
                            }
                        }
                    }
                    if (window.filterController && typeof window.filterController.refreshAvailability === 'function') {
                        window.filterController.refreshAvailability();
                    }
                    if (window.filterController && typeof window.filterController.updateFilterCounter === 'function') {
                        window.filterController.updateFilterCounter();
                    }
                } catch (e) {
                    console.warn('[BrowserHistoryHandler] Could not clear filter UI:', e);
                }
            }
        }
        
        // Sync lastUrlState with current URL state
        const currentUrlState = [
            query ? `sq=${encodeURIComponent(query.trim())}` : '',
            dateFrom ? `dateFrom=${encodeURIComponent(dateFrom.trim())}` : '',
            dateTo ? `dateTo=${encodeURIComponent(dateTo.trim())}` : '',
            cat ? `cat=${cat}` : ''
        ].filter(Boolean).sort().join('&');
        
        if (window.__filterControllerUrlState) {
            window.__filterControllerUrlState.lastUrlState = currentUrlState;
            if (typeof window.__filterControllerUrlState.setLastUrlState === 'function') {
                window.__filterControllerUrlState.setLastUrlState(currentUrlState);
            }
        }
        
        // Handle tab switching and search execution based on hash and query
        const isFulltextSearch = hash === '#fulltext';
        const hasQuery = query && query.trim().length > 0;
        
        // Switch tab based on hash
        if (isFulltextSearch) {
            const $fulltextTab = window.jQuery ? window.jQuery('#nav-full-text-card-tab') : null;
            if ($fulltextTab && $fulltextTab.length && !$fulltextTab.hasClass('active')) {
                try {
                    if (window.bootstrap && typeof window.bootstrap.Tab === 'function') {
                        new window.bootstrap.Tab($fulltextTab[0]).show();
                    }
                } catch (e) {
                    console.error('[BrowserHistoryHandler] Error switching to fulltext tab:', e);
                }
            }
        } else {
            const $filesTab = window.jQuery ? window.jQuery('#nav-names-card-tab') : null;
            if ($filesTab && $filesTab.length && !$filesTab.hasClass('active')) {
                try {
                    if (window.bootstrap && typeof window.bootstrap.Tab === 'function') {
                        new window.bootstrap.Tab($filesTab[0]).show();
                    }
                } catch (e) {}
            }
        }
        
        // Execute search based on hash and query
        if (isFulltextSearch && hasQuery) {
            // Perform fulltext search - use setTimeout to ensure tab switch completes
            const pageNum = parseInt(page, 10) || 1;
            if (window.searchApi && window.axios && typeof window.performFulltextSearch === 'function') {
                window.__forceResetSearchFlag = true;
                setTimeout(() => {
                    try {
                        window.performFulltextSearch(query, pageNum);
                    } catch (e) {
                        console.error('[BrowserHistoryHandler] Error performing fulltext search:', e);
                    }
                }, 100);
            }
        } else if (hasQuery && !isFulltextSearch) {
            // Regular file search - apply filters
            // Don't update URL here - we're restoring state from URL
            if (window.filterManager && typeof window.filterManager.applyFilters === 'function') {
                setTimeout(() => {
                    try {
                        window.filterManager.applyFilters(false); // false = don't update URL (state restoration)
                    } catch (e) {
                        if (debugMode === '1') {
                            console.error('[BrowserHistoryHandler] Error applying filters:', e);
                        }
                    }
                }, 100);
            }
        } else if (dateFrom || dateTo) {
            // Even without query, if dates are present, apply filters
            // Don't update URL here - we're restoring state from URL
            if (window.filterManager && typeof window.filterManager.applyFilters === 'function') {
                setTimeout(() => {
                    try {
                        window.filterManager.applyFilters(false); // false = don't update URL (state restoration)
                    } catch (e) {
                        if (debugMode === '1') {
                            console.error('[BrowserHistoryHandler] Error applying date filters:', e);
                        }
                    }
                }, 100);
            }
        } else {
            // No query - clear search results
            try {
                const $container = window.jQuery ? window.jQuery('#nav-full-text') : null;
                if ($container && $container.length) {
                    $container.find('.search-results-list').empty();
                    $container.find('.pages').empty();
                }
            } catch (e) {}
        }
        
        
    } catch (error) {
        console.error('[BrowserHistoryHandler] ❌ Error restoring state:', error);
    } finally {
        // Reset flag after a delay to allow all async operations to complete
        // This includes filter UI updates, tab switches, and search execution
        setTimeout(() => {
            isRestoringState = false;
            window.__isRestoringBrowserState = false;
        }, 500);
    }
}

/**
 * Handle browser back/forward navigation
 * Simply reads URL and restores state from it
 */
function handlePopState(event) {
    const currentPath = window.location.pathname;
    const expectedPaths = ['/en/', '/ru/', '/fr/', '/de/', '/pl/', '/'];
    
    // Check if we're still on the same page
    const isExpectedPath = expectedPaths.some(path => currentPath === path || currentPath.startsWith(path));
    
    // If we're on a different page, let browser handle navigation
    if (!isExpectedPath) {
        return;
    }
    
    // Restore state from URL (same as initial page load)
    restoreStateFromUrl();
}

/**
 * Initialize browser history handler
 * Sets up popstate event listener to restore form state from URL
 */
export function initializeBrowserHistoryHandler() {
    // Prevent multiple initializations
    if (window.__browserHistoryHandlerInitialized) {
        return;
    }
    
    window.__browserHistoryHandlerInitialized = true;
    
    // Replace initial state in history to avoid creating empty entry
    try {
        const currentUrl = window.location.href;
        const currentPath = window.location.pathname;
        const expectedPaths = ['/en/', '/ru/', '/fr/', '/de/', '/pl/', '/'];
        
        if (expectedPaths.some(path => currentPath === path || currentPath.startsWith(path))) {
            window.history.replaceState({ 
                page: 'search',
                url: currentUrl 
            }, '', currentUrl);
        }
    } catch (e) {
        console.warn('[BrowserHistoryHandler] Could not create initial state:', e);
    }
    
    // Handle browser back/forward navigation
    window.addEventListener('popstate', handlePopState);
    
}

/**
 * Check if state is currently being restored
 * Used to prevent URL updates during restoration
 */
export function isRestoringBrowserState() {
    return isRestoringState;
}

// Export for global access
window.initializeBrowserHistoryHandler = initializeBrowserHistoryHandler;
window.isRestoringBrowserState = isRestoringBrowserState;

// Auto-initialize if DOM is ready (fallback)
if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(() => {
        if (!window.__browserHistoryHandlerInitialized) {
            initializeBrowserHistoryHandler();
        }
    }, 1000);
} else {
    document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
            if (!window.__browserHistoryHandlerInitialized) {
                initializeBrowserHistoryHandler();
            }
        }, 1000);
    });
}
